import React, { useState, useCallback, useContext, useEffect } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { TeamModuleContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { LocationAndTeamsOption } from '@vyce/core/src/types';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { updateTeamRequest } from '@vyce/core/src/api/teams';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

interface Props {
  open: boolean;
  onClose: () => void;
  locations: LocationAndTeamsOption[];
  selectedLocationIds: string[];
  onSuccess?: () => void;
}

const useStyles = makeStyles(theme =>
  createStyles({
    dialogContent: {
      paddingTop: 8,
    },
    title: {
      fontSize: 18,
      letterSpacing: 0.75,
      lineHeight: '32px',
      marginBottom: 16,
    },
    optionsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    },
    optionItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      minWidth: 0,
      maxWidth: 280,
    },
    optionLabel: {
      display: 'block',
      fontSize: 16,
      letterSpacing: 0.75,
      lineHeight: '32px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
      maxWidth: 250,
      color: theme.palette.text.secondary,
    },
  })
);

export const AssignLocationDialog: React.FC<Props> = ({
  open,
  onClose,
  locations,
  selectedLocationIds,
  onSuccess,
}) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const { selectedCompanyId, teamData, setTeamData } = useContext(TeamModuleContext);
  const { handleServerError, showNotification } = useContext(NotificationContext);

  const [selectedIds, setSelectedIds] = useState<string[]>(selectedLocationIds);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedIds(selectedLocationIds);
    }
  }, [open, selectedLocationIds]);

  const handleToggle = useCallback((locationId: string) => {
    setSelectedIds(prev =>
      prev.includes(locationId) ? prev.filter(id => id !== locationId) : [...prev, locationId]
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedCompanyId || !teamData?.uuid) {
      return;
    }
    setSaving(true);
    try {
      const { data } = await updateTeamRequest(selectedCompanyId, teamData.uuid, {
        name: teamData.name,
        description: teamData.description,
        location_ids: selectedIds,
      });
      setTeamData?.(data as any);
      showNotification({ message: 'Locations updated', options: { variant: 'success' } });
      onSuccess?.();
      onClose();
    } catch (e) {
      handleServerError(e);
    } finally {
      setSaving(false);
    }
  }, [
    selectedCompanyId,
    teamData,
    selectedIds,
    setTeamData,
    onSuccess,
    onClose,
    handleServerError,
    showNotification,
  ]);

  const activeLocations = locations.filter(loc => loc.is_active !== false);

  return (
    <Dialog
      fullScreen={isMobile}
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-location-dialog-title"
    >
      <DialogTitle id="assign-location-dialog-title">Assign Location</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.title}>Which Locations?</Typography>
        <Box className={classes.optionsRow}>
          {activeLocations.length === 0 ? (
            <Typography color="textSecondary">No locations available</Typography>
          ) : (
            activeLocations.map(loc => (
              <Box key={loc.uuid} className={classes.optionItem}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIds.includes(loc.uuid)}
                      onChange={() => handleToggle(loc.uuid)}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box component="span" className={classes.optionLabel}>
                      {loc.name}
                    </Box>
                  }
                />
              </Box>
            ))
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="large" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          size="large"
          color="primary"
          variant="contained"
          style={{ width: 100 }}
          disabled={saving}
          cy-test-id="assign-location-save"
        >
          <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={saving} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
